import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { teamsApi, eventsApi } from '../utils/api';

export default function ParticipantTeams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('my-teams');
  const [loading, setLoading] = useState(true);

  // My Teams state
  const [myTeams, setMyTeams] = useState([]);

  // Browse state
  const [browseTeams, setBrowseTeams] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  // Invitations state
  const [invitations, setInvitations] = useState([]);

  // Action state
  const [actionLoading, setActionLoading] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [createForm, setCreateForm] = useState({ team_name: '', event_id: '', max_members: 4 });
  const [events, setEvents] = useState([]);
  const [acceptResult, setAcceptResult] = useState(null);

  const fetchMyTeams = useCallback(async () => {
    try {
      const { data } = await teamsApi.myTeams();
      setMyTeams(data.data);
    } catch (err) {
      console.error('Failed to load my teams:', err);
    }
  }, []);

  const fetchBrowseTeams = useCallback(async () => {
    try {
      const { data } = await teamsApi.browse({ search, page, limit: 20 });
      setBrowseTeams(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  }, [search, page]);

  const fetchMyRequests = useCallback(async () => {
    try {
      const { data } = await teamsApi.myRequests();
      setMyRequests(data.data);
    } catch (err) {
      console.error('Failed to load requests:', err);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const { data } = await teamsApi.getInvitations();
      setInvitations(data.data);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    }
  }, []);

  // Handle ?accept=TOKEN on mount
  useEffect(() => {
    const token = searchParams.get('accept');
    if (token) {
      setActiveTab('invitations');
      (async () => {
        try {
          const { data } = await teamsApi.acceptInvitation(token);
          setAcceptResult({ success: true, message: data.message, team_name: data.data?.team_name });
        } catch (err) {
          setAcceptResult({ success: false, message: err.response?.data?.message || 'Failed to accept invitation' });
        }
        // Clean URL
        searchParams.delete('accept');
        setSearchParams(searchParams, { replace: true });
        // Refresh data
        fetchMyTeams();
        fetchInvitations();
      })();
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    Promise.all([fetchMyTeams(), fetchBrowseTeams(), fetchMyRequests(), fetchInvitations()])
      .finally(() => setLoading(false));
  }, [fetchMyTeams, fetchBrowseTeams, fetchMyRequests, fetchInvitations]);

  // Set default tab based on data
  useEffect(() => {
    if (!loading && myTeams.length === 0 && invitations.length > 0) {
      setActiveTab('invitations');
    } else if (!loading && myTeams.length === 0) {
      setActiveTab('browse');
    }
  }, [loading, myTeams.length, invitations.length]);

  async function handleCreateTeam(e) {
    e.preventDefault();
    setActionLoading('create');
    try {
      await teamsApi.createTeam(createForm);
      setShowCreateModal(false);
      setCreateForm({ team_name: '', event_id: '', max_members: 4 });
      await fetchMyTeams();
      setActiveTab('my-teams');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create team');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleInvite(teamId) {
    if (!inviteEmail.trim()) return;
    setActionLoading(`invite-${teamId}`);
    try {
      await teamsApi.invite(teamId, { email: inviteEmail.trim() });
      setInviteEmail('');
      setShowInviteModal(null);
      await fetchMyTeams();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCancelInvitation(invitationId) {
    setActionLoading(invitationId);
    try {
      await teamsApi.cancelInvitation(invitationId);
      await fetchMyTeams();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRemoveMember(teamId, userId, name) {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    setActionLoading(`remove-${userId}`);
    try {
      await teamsApi.removeMember(teamId, userId);
      await fetchMyTeams();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRespondToRequest(requestId, action) {
    setActionLoading(requestId);
    try {
      await teamsApi.respondToRequest(requestId, { action });
      await fetchMyTeams();
    } catch (err) {
      alert(err.response?.data?.message || `Failed to ${action} request`);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAcceptInvitation(token) {
    setActionLoading(token);
    try {
      const { data } = await teamsApi.acceptInvitation(token);
      setAcceptResult({ success: true, message: data.message });
      await Promise.all([fetchInvitations(), fetchMyTeams()]);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleJoinRequest(teamId) {
    setActionLoading(teamId);
    try {
      await teamsApi.requestJoin(teamId, { message: joinMessage || undefined });
      setJoinMessage('');
      setShowMessageModal(null);
      await Promise.all([fetchBrowseTeams(), fetchMyRequests()]);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send request');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCancelJoinRequest(requestId) {
    if (!window.confirm('Cancel this join request?')) return;
    setActionLoading(requestId);
    try {
      await teamsApi.cancelRequest(requestId);
      await Promise.all([fetchBrowseTeams(), fetchMyRequests()]);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    fetchBrowseTeams();
  }

  async function openCreateModal() {
    setShowCreateModal(true);
    if (events.length === 0) {
      try {
        const { data } = await eventsApi.getEvents();
        const activeEvents = (data.data || []).filter(e => ['active', 'live'].includes(e.status));
        setEvents(activeEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
      }
    }
  }

  if (loading) return <div className="spinner" />;

  const tabs = [
    { key: 'my-teams', label: 'My Teams', count: myTeams.length },
    { key: 'browse', label: 'Browse Teams' },
    { key: 'invitations', label: 'Invitations', count: invitations.length, hideIfZero: true }
  ];

  const visibleTabs = tabs.filter(t => !t.hideIfZero || t.count > 0);

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header page-header-row">
          <div>
            <h1>Teams</h1>
            <p>Create, manage, and join teams</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>+ Create Team</button>
        </div>

        {/* Accept result banner */}
        {acceptResult && (
          <div className={`alert ${acceptResult.success ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>
            {acceptResult.message}
            <button className="btn btn-ghost btn-sm" onClick={() => setAcceptResult(null)} style={{ marginLeft: 12, padding: '2px 8px' }}>Dismiss</button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #2a3f56' }}>
          {visibleTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid #e8a838' : '2px solid transparent',
                color: activeTab === tab.key ? '#e8a838' : '#8a9bb5',
                fontWeight: activeTab === tab.key ? 600 : 400,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  marginLeft: 6, background: activeTab === tab.key ? '#e8a838' : '#2a3f56',
                  color: activeTab === tab.key ? '#0d1b2a' : '#8a9bb5',
                  padding: '1px 7px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600
                }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* TAB: My Teams */}
        {activeTab === 'my-teams' && (
          <div>
            {myTeams.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ color: '#8a9bb5', marginBottom: 16 }}>You haven't created any teams yet.</p>
                <button className="btn btn-primary" onClick={openCreateModal}>Create Your First Team</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {myTeams.map(team => (
                  <div key={team.id} className="card">
                    <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
                      <div>
                        <h3 style={{ margin: 0 }}>{team.team_name}</h3>
                        <span style={{ color: '#8a9bb5', fontSize: '0.82rem' }}>{team.event_name || 'No event'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: team.member_count >= team.max_members ? '#e85d5d' : '#3adc8a', fontSize: '0.85rem' }}>
                          {team.member_count}/{team.max_members} members
                        </span>
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                          onClick={() => { setShowInviteModal(team.id); setInviteEmail(''); }}
                          disabled={team.member_count >= team.max_members}
                        >
                          Invite
                        </button>
                      </div>
                    </div>

                    {/* Members */}
                    <div style={{ padding: '0 16px 12px' }}>
                      <div style={{ fontSize: '0.78rem', color: '#8a9bb5', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Members</div>
                      {team.members.map(m => (
                        <div key={m.id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 10px', background: '#1e2d3d', borderRadius: 6, marginBottom: 4
                        }}>
                          <div>
                            <span style={{ color: '#f0f2f5', fontWeight: 500 }}>{m.first_name} {m.last_name}</span>
                            <span style={{ color: '#8a9bb5', fontSize: '0.8rem', marginLeft: 8 }}>{m.email}</span>
                            {m.role === 'captain' && <span className="badge badge-active" style={{ marginLeft: 8, fontSize: '0.65rem', padding: '1px 6px' }}>Captain</span>}
                          </div>
                          {m.role !== 'captain' && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#e85d5d', padding: '2px 8px', fontSize: '0.7rem' }}
                              onClick={() => handleRemoveMember(team.id, m.user_id, `${m.first_name} ${m.last_name}`)}
                              disabled={actionLoading === `remove-${m.user_id}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Pending Invitations */}
                    {team.invitations && team.invitations.length > 0 && (
                      <div style={{ padding: '0 16px 12px' }}>
                        <div style={{ fontSize: '0.78rem', color: '#8a9bb5', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Pending Invitations</div>
                        {team.invitations.map(inv => (
                          <div key={inv.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '6px 10px', background: '#1e2d3d', borderRadius: 6, marginBottom: 4
                          }}>
                            <span style={{ color: '#f0f2f5' }}>{inv.email}</span>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#e85d5d', padding: '2px 8px', fontSize: '0.7rem' }}
                              onClick={() => handleCancelInvitation(inv.id)}
                              disabled={actionLoading === inv.id}
                            >
                              Cancel
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pending Join Requests */}
                    {team.join_requests && team.join_requests.length > 0 && (
                      <div style={{ padding: '0 16px 16px' }}>
                        <div style={{ fontSize: '0.78rem', color: '#8a9bb5', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Join Requests</div>
                        {team.join_requests.map(jr => (
                          <div key={jr.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 10px', background: '#1e2d3d', borderRadius: 6, marginBottom: 4,
                            flexWrap: 'wrap', gap: 8
                          }}>
                            <div>
                              <span style={{ color: '#f0f2f5', fontWeight: 500 }}>
                                {jr.requester_first_name} {jr.requester_last_name}
                              </span>
                              <span style={{ color: '#8a9bb5', fontSize: '0.8rem', marginLeft: 8 }}>{jr.requester_email}</span>
                              {jr.message && (
                                <div style={{ color: '#8a9bb5', fontSize: '0.78rem', fontStyle: 'italic', marginTop: 2 }}>"{jr.message}"</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                className="btn btn-primary btn-sm"
                                style={{ padding: '3px 10px', fontSize: '0.7rem' }}
                                onClick={() => handleRespondToRequest(jr.id, 'approve')}
                                disabled={actionLoading === jr.id}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ padding: '3px 10px', fontSize: '0.7rem', color: '#e85d5d' }}
                                onClick={() => handleRespondToRequest(jr.id, 'deny')}
                                disabled={actionLoading === jr.id}
                              >
                                Deny
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Browse Teams */}
        {activeTab === 'browse' && (
          <div>
            {/* My Pending Requests */}
            {myRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-header">
                  <h3>My Pending Requests ({myRequests.filter(r => r.status === 'pending').length})</h3>
                </div>
                <div className="table-wrap">
                  <table className="responsive-table">
                    <thead>
                      <tr>
                        <th>Team</th>
                        <th>Event</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myRequests.filter(r => r.status === 'pending').map(r => (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 500 }}>{r.team_name}</td>
                          <td data-label="Event" style={{ color: '#8a9bb5' }}>{r.event_name || '—'}</td>
                          <td data-label="Requested" style={{ color: '#8a9bb5' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                          <td>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#e85d5d', padding: '4px 12px', fontSize: '0.75rem' }}
                              onClick={() => handleCancelJoinRequest(r.id)}
                              disabled={actionLoading === r.id}
                            >
                              {actionLoading === r.id ? 'Cancelling...' : 'Cancel'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Browse */}
            <div className="card">
              <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
                <h3>Browse Teams</h3>
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="Search teams or events..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minWidth: 200 }}
                  />
                  <button type="submit" className="btn btn-primary btn-sm">Search</button>
                </form>
              </div>

              {browseTeams.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
                  <p>No teams found. Try a different search.</p>
                </div>
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="responsive-table">
                      <thead>
                        <tr>
                          <th>Team Name</th>
                          <th>Event</th>
                          <th>Captain</th>
                          <th>Members</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {browseTeams.map(t => (
                          <tr key={t.id}>
                            <td style={{ fontWeight: 500 }}>{t.team_name}</td>
                            <td data-label="Event" style={{ color: '#8a9bb5' }}>{t.event_name || '—'}</td>
                            <td data-label="Captain" style={{ color: '#8a9bb5' }}>
                              {t.captain_first_name ? `${t.captain_first_name} ${t.captain_last_name}` : '—'}
                            </td>
                            <td data-label="Members">
                              <span style={{ color: t.member_count >= t.max_members ? '#e85d5d' : '#3adc8a' }}>
                                {t.member_count}/{t.max_members}
                              </span>
                            </td>
                            <td>
                              {t.is_member ? (
                                <span className="badge badge-active">Member</span>
                              ) : t.join_request_status === 'pending' ? (
                                <span className="badge badge-active" style={{ background: '#2a3f56' }}>Pending</span>
                              ) : t.member_count >= t.max_members ? (
                                <span style={{ color: '#8a9bb5', fontSize: '0.8rem' }}>Full</span>
                              ) : (
                                <button
                                  className="btn btn-primary btn-sm"
                                  style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                                  onClick={() => setShowMessageModal(t.id)}
                                  disabled={actionLoading === t.id}
                                >
                                  Request to Join
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {pagination.pages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0' }}>
                      <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                      <span style={{ color: '#8a9bb5', lineHeight: '32px', fontSize: '0.85rem' }}>Page {page} of {pagination.pages}</span>
                      <button className="btn btn-ghost btn-sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* TAB: Invitations */}
        {activeTab === 'invitations' && (
          <div>
            {invitations.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#8a9bb5' }}>
                <p>No pending invitations.</p>
              </div>
            ) : (
              <div className="card">
                <div className="card-header">
                  <h3>Pending Invitations</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 0 16px' }}>
                  {invitations.map(inv => (
                    <div key={inv.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 16px', background: '#1e2d3d', borderRadius: 8,
                      border: '1px solid #2a3f56', flexWrap: 'wrap', gap: 10
                    }}>
                      <div>
                        <div style={{ color: '#f0f2f5', fontWeight: 500 }}>
                          Team: <span style={{ color: '#e8a838' }}>{inv.team_name}</span>
                        </div>
                        <div style={{ color: '#8a9bb5', fontSize: '0.82rem', marginTop: 2 }}>
                          {inv.event_name || '—'} — invited by {inv.inviter_first_name} {inv.inviter_last_name}
                        </div>
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                        onClick={() => handleAcceptInvitation(inv.token)}
                        disabled={actionLoading === inv.token}
                      >
                        {actionLoading === inv.token ? 'Accepting...' : 'Accept'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Join Request Message Modal */}
        {showMessageModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setShowMessageModal(null)}>
            <div className="card" style={{ maxWidth: 440, width: '90%', padding: 24 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 16 }}>Request to Join</h3>
              <textarea
                className="input"
                placeholder="Optional message to the captain..."
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                rows={3}
                style={{ width: '100%', marginBottom: 16, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowMessageModal(null); setJoinMessage(''); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleJoinRequest(showMessageModal)}
                  disabled={actionLoading === showMessageModal}
                >
                  {actionLoading === showMessageModal ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Team Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setShowCreateModal(false)}>
            <div className="card" style={{ maxWidth: 480, width: '90%', padding: 24 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 16 }}>Create Team</h3>
              <form onSubmit={handleCreateTeam}>
                <div className="form-group">
                  <label>Event</label>
                  <select
                    value={createForm.event_id}
                    onChange={e => setCreateForm({ ...createForm, event_id: e.target.value })}
                    required
                  >
                    <option value="">Select an event...</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                  {events.length === 0 && (
                    <div style={{ color: '#8a9bb5', fontSize: '0.78rem', marginTop: 4 }}>No active events available. You must be registered for an event first.</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Team Name</label>
                  <input
                    type="text"
                    value={createForm.team_name}
                    onChange={e => setCreateForm({ ...createForm, team_name: e.target.value })}
                    placeholder="Enter team name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Max Members</label>
                  <input
                    type="number"
                    value={createForm.max_members}
                    onChange={e => setCreateForm({ ...createForm, max_members: parseInt(e.target.value) || 4 })}
                    min={1}
                    max={20}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={actionLoading === 'create'}>
                    {actionLoading === 'create' ? 'Creating...' : 'Create Team'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invite Member Modal */}
        {showInviteModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setShowInviteModal(null)}>
            <div className="card" style={{ maxWidth: 440, width: '90%', padding: 24 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 16 }}>Invite Member</h3>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  className="input"
                  placeholder="teammate@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  style={{ width: '100%' }}
                />
                <div style={{ color: '#8a9bb5', fontSize: '0.78rem', marginTop: 4 }}>
                  They'll receive an email invitation. If they don't have an account, they'll be prompted to register first.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowInviteModal(null); setInviteEmail(''); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleInvite(showInviteModal)}
                  disabled={actionLoading === `invite-${showInviteModal}` || !inviteEmail.trim()}
                >
                  {actionLoading === `invite-${showInviteModal}` ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
